import { useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFreezeValidationReport } from '@/services/reports';

import {
  useCreateFreezeValidationReport,
  useValidateFreeze,
} from '../../../services/freeze/hooks';
import { SelectionService } from '../utils/selectionService';
import { getValidateFreezeSiteId } from '../utils/utils';

import './FreezeValidation.css';

type FreezeValidationProps = {
  className?: string;
  checkEmbryoConditions: () => any;
  checkEmbryoQCFields: () => any;
  frozeValidatorId: string;
  frozeValidationDate: Date;
  missingLocationsMessages: string[];
  allFertMathCorrect: boolean;
};

export const FreezeValidation = ({
  className = '',
  checkEmbryoConditions,
  checkEmbryoQCFields,
  frozeValidationDate,
  frozeValidatorId,
  missingLocationsMessages,
  allFertMathCorrect,
}: FreezeValidationProps) => {
  const [params] = useSearchParams();
  const fertilizationDate = params.get('date') || new Date().toString();
  const rawFertilizationSiteId = params.get('sId');

  const [validationDate, setValidationDate] = useState<Date>();
  const [validatorId, setValidatorId] = useState<string>();
  const [showQCDialog, setShowQCDialog] = useState(false);
  type QCMissingField = { embryoName: string; missingFields: string[] };
  const [qcMissingFields, setQcMissingFields] = useState<QCMissingField[]>([]);

  const [showStorageLocationDialog, setShowStorageLocationDialog] =
    useState(false);

  const { mutateAsync: createFreezeValidationReport } =
    useCreateFreezeValidationReport();
  const isReportFixEnabled = useFeatureFlag('Freeze_Valid_Report_Not_Gen');
  const [generateFreezeValidationReport] = useFreezeValidationReport();

  const { mutateAsync: validateFreeze } = useValidateFreeze();
  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });
  const mappedTechs = (techsData || []).map((tech) => ({
    label: tech.name,
    value: tech.id,
  }));

  const handleSubmit = () => {
    const qcFieldsFilled = checkEmbryoQCFields();
    if (qcFieldsFilled && qcFieldsFilled.length > 0) {
      setQcMissingFields(qcFieldsFilled);
      setShowQCDialog(true);
      return;
    }
    setQcMissingFields([]);
    const conditions = checkEmbryoConditions();
    if (conditions.allEmbryosContainStorageLocation === false) {
      return;
    }
    if (missingLocationsMessages.length === 0 && allFertMathCorrect === true) {
      const adjustedValidationDate = new Date(validationDate!);
      const now = new Date();
      adjustedValidationDate.setHours(
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds(),
      );

      const selection = SelectionService.getSelection();
      const { fertilizationIds } = selection;
      validateFreeze({
        fertilizationIds,
        fertilizationDate,
        fertilizationSiteId: getValidateFreezeSiteId(rawFertilizationSiteId),
        validatorId,
        validationDate: adjustedValidationDate,
      });
    }
  };

  async function createFreezeReport() {
    const fertilizationIds = SelectionService.getSelection().fertilizationIds;

    if (isReportFixEnabled) {
      await generateFreezeValidationReport({ fertilizationIds });
      return;
    }

    try {
      const result = await createFreezeValidationReport({
        fertilizationIds,
      });
      const pdfData = result?.data?.createFreezeValidationReport;
      const fileName = `freeze-validation-report-${new Date().toISOString()}.pdf`;

      if (!pdfData) {
        console.error('No PDF data received from the server');
        return;
      }

      const blob =
        pdfData instanceof Blob
          ? pdfData
          : new Blob([new Uint8Array(pdfData)], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);
    } catch (error) {
      console.error('Error creating fresh shipment report:', error);
    }
  }

  return (
    <div className={`freeze-validation ${className}`.trim()}>
      <div className="label-input-group">
        <label htmlFor="input">Please Enter Name</label>
        <Dropdown
          value={validatorId}
          options={mappedTechs}
          onChange={(e) => {
            setValidatorId(e.value);
          }}
          virtualScrollerOptions={{
            itemSize: 25,
          }}
          filter
          placeholder="Select Name"
        />
      </div>
      <div className="label-input-group">
        <label htmlFor="calendar">Date Validated</label>
        <Calendar
          id="calendar"
          value={validationDate}
          onChange={(e) => setValidationDate(e.value as Date)}
          placeholder="Select a date"
          showIcon
        />
      </div>
      <div className="freeze-validation__button-container">
        <Button
          label="Submit"
          icon="pi pi-check"
          onClick={handleSubmit}
          className="submit-button"
          disabled={!validatorId || !validationDate}
        />
      </div>{' '}
      <div className="freeze-validation__button-container">
        <Button
          label="Freeze Validation Report"
          icon="pi pi-file-pdf"
          onClick={createFreezeReport}
          className="submit-button"
          disabled={!SelectionService.getSelection().fertilizationIds.length}
        />
      </div>
      {frozeValidatorId && mappedTechs.length > 0 && frozeValidationDate && (
        <div className="previously-validated-by">
          <label>
            Last validated by{' '}
            {mappedTechs.find((tech) => tech.value === frozeValidatorId)?.label}{' '}
            on {frozeValidationDate.toLocaleDateString()}
          </label>
        </div>
      )}
      <Dialog
        visible={showQCDialog}
        onHide={() => setShowQCDialog(false)}
        header="QC Fields Missing From Selected Live Embryos"
        modal={false}
        dismissableMask
      >
        <p>Reminder</p>
        <p>
          For matings with Freeze Method of 1.5M EG w/ sucrose: Include EG Lot
          Number, EG Expiration Date, EHM Expiration Date, Sucrose Expiration
          Date, Straw Lot #, & 12 Well Lot #
        </p>
        <p>
          For matings with Freeze Method of Vit: Include EHM Expiration Date, VS
          Expiration Date, ES Expiration Date, Vit Straw Lot #
        </p>
        <p>
          For matings that have export column as YES: IncludeEG Lot Number, EG
          Expiration Date, EHM Expiration Date, Sucrose Expiration Date, Sterile
          Straw Lot #, and 12 Well Lot #
        </p>
        {qcMissingFields.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <strong>Missing Fields:</strong>
            <ul>
              {qcMissingFields.map((emb, idx) => (
                <li key={idx}>
                  <strong>{emb.embryoName}:</strong>{' '}
                  {emb.missingFields.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Dialog>
      <Dialog
        visible={showStorageLocationDialog}
        onHide={() => setShowStorageLocationDialog(false)}
        header="Error"
        className="error-dialog"
      >
        <div className="dialog-content">
          <i className="pi pi-exclamation-triangle dialog-icon" />
          <p>Storage Location must be selected</p>
        </div>
      </Dialog>
    </div>
  );
};
